import { sortPaymentAssignmentStatusesByDisplayOrder } from './paymentAssignmentStatusOrder'
import { PaymentAssignmentStatusId, PaymentStatus } from './types'
import { PaymentAssignmentRole } from './roles'
import useFeatureFlags from 'hooks/useFeatureFlags'

export interface PaymentAssignmentRolePolicy {
  canAssignMaker: boolean
  canAssignEscalator: boolean
  canAssignPriority: boolean
  canBulkAssign: boolean
  canUpdateStatus: boolean
  canEditPrimaryPaymentMethod: boolean
  canSubmitPayment: boolean
  canCancelPayment: boolean
  showMakerColumn: boolean
  showEscalatorColumn: boolean
  showContactEvidence: boolean
  showTicketEvidence: boolean
  showTicketColumn: boolean
  allowedStatuses: PaymentStatus[]
  /** Whether the role may use the maker (assignee) filter in the drawer. */
  canFilterByAssignee: boolean
  /** Whether the role may use the escalator filter in the drawer. */
  canFilterByEscalator: boolean
}

const MAKER_STATUSES: PaymentStatus[] =
  sortPaymentAssignmentStatusesByDisplayOrder([
    PaymentAssignmentStatusId.PAYMENT_SUBMITTED,
    PaymentAssignmentStatusId.PENDING_PAYMENT,
    PaymentAssignmentStatusId.PENDING_AUTOPAY,
    PaymentAssignmentStatusId.PAID_ESCALATION,
    PaymentAssignmentStatusId.UNPAID_ESCALATION,
    PaymentAssignmentStatusId.REATTEMPT,
    PaymentAssignmentStatusId.EE_PAID,
    PaymentAssignmentStatusId.TERMINATED_EE,
    PaymentAssignmentStatusId.TERMINATED_PLAN,
    PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE,
    PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT,
    PaymentAssignmentStatusId.BOT_QUEUED,
    PaymentAssignmentStatusId.BOT_PROCESSING,
    PaymentAssignmentStatusId.BOT_FAILED,
    PaymentAssignmentStatusId.BOT_SUBMITTED,
    PaymentAssignmentStatusId.BOT_CLEARED,
    PaymentAssignmentStatusId.BOT_CANCELLED,
  ])

const CHECKER_STATUSES: PaymentStatus[] =
  sortPaymentAssignmentStatusesByDisplayOrder([
    PaymentAssignmentStatusId.PENDING_ASSIGNMENT,
    PaymentAssignmentStatusId.PENDING_PAYMENT,
    PaymentAssignmentStatusId.PAYMENT_SUBMITTED,
    PaymentAssignmentStatusId.PENDING_AUTOPAY,
    PaymentAssignmentStatusId.PAID_ESCALATION,
    PaymentAssignmentStatusId.UNPAID_ESCALATION,
    PaymentAssignmentStatusId.EE_PAID,
    PaymentAssignmentStatusId.TERMINATED_EE,
    PaymentAssignmentStatusId.TERMINATED_PLAN,
    PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE,
    PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT,
    PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED,
    PaymentAssignmentStatusId.PAYMENT_CLEARED,
    PaymentAssignmentStatusId.AP_CONFIRMED,
    PaymentAssignmentStatusId.REATTEMPT,
    PaymentAssignmentStatusId.BOT_QUEUED,
    PaymentAssignmentStatusId.BOT_PROCESSING,
    PaymentAssignmentStatusId.BOT_FAILED,
    PaymentAssignmentStatusId.BOT_SUBMITTED,
    PaymentAssignmentStatusId.BOT_CLEARED,
    PaymentAssignmentStatusId.BOT_CANCELLED,
  ])

const ESCALATOR_STATUSES: PaymentStatus[] =
  sortPaymentAssignmentStatusesByDisplayOrder([
    PaymentAssignmentStatusId.PENDING_ASSIGNMENT,
    PaymentAssignmentStatusId.PENDING_PAYMENT,
    PaymentAssignmentStatusId.PAYMENT_SUBMITTED,
    PaymentAssignmentStatusId.PENDING_AUTOPAY,
    PaymentAssignmentStatusId.PAID_ESCALATION,
    PaymentAssignmentStatusId.UNPAID_ESCALATION,
    PaymentAssignmentStatusId.EE_PAID,
    PaymentAssignmentStatusId.TERMINATED_EE,
    PaymentAssignmentStatusId.TERMINATED_PLAN,
    PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE,
    PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT,
    PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED,
    PaymentAssignmentStatusId.PAYMENT_CLEARED,
    PaymentAssignmentStatusId.AP_CONFIRMED,
    PaymentAssignmentStatusId.REATTEMPT,
    PaymentAssignmentStatusId.BOT_QUEUED,
    PaymentAssignmentStatusId.BOT_PROCESSING,
    PaymentAssignmentStatusId.BOT_FAILED,
    PaymentAssignmentStatusId.BOT_SUBMITTED,
    PaymentAssignmentStatusId.BOT_CLEARED,
    PaymentAssignmentStatusId.BOT_CANCELLED,
  ])

/** All statuses for view-only (UNKNOWN) role so they can filter by any status. */
const ALL_STATUSES: PaymentStatus[] =
  sortPaymentAssignmentStatusesByDisplayOrder(
    Object.values(PaymentAssignmentStatusId) as PaymentStatus[]
  )

const CHECKER_POLICY: PaymentAssignmentRolePolicy = {
  canAssignMaker: true,
  canAssignEscalator: true,
  canAssignPriority: true,
  canBulkAssign: true,
  canUpdateStatus: true,
  canEditPrimaryPaymentMethod: true,
  canSubmitPayment: true,
  canCancelPayment: true,
  showMakerColumn: true,
  showEscalatorColumn: true,
  showContactEvidence: false,
  showTicketEvidence: false,
  allowedStatuses: CHECKER_STATUSES,
  showTicketColumn: true,
  canFilterByAssignee: true,
  canFilterByEscalator: true,
}

const MAKER_POLICY: PaymentAssignmentRolePolicy = {
  canAssignMaker: false,
  canAssignEscalator: true,
  canAssignPriority: false,
  canBulkAssign: false,
  canUpdateStatus: true,
  canEditPrimaryPaymentMethod: false,
  canSubmitPayment: true,
  canCancelPayment: true,
  showMakerColumn: true,
  showEscalatorColumn: false,
  showContactEvidence: false,
  showTicketEvidence: true,
  allowedStatuses: MAKER_STATUSES,
  showTicketColumn: false,
  // MAKER's assignee filter is always locked to self (enforced in PaymentAssignment init),
  // but the filter control is still shown (locked/disabled) so the user can see their scope.
  canFilterByAssignee: true,
  // MAKER has no escalator column and no escalator filter — they work on their own queue.
  canFilterByEscalator: false,
}

const ESCALATOR_POLICY: PaymentAssignmentRolePolicy = {
  canAssignMaker: false,
  canAssignEscalator: false,
  canAssignPriority: false,
  canBulkAssign: false,
  canUpdateStatus: true,
  canEditPrimaryPaymentMethod: false,
  canSubmitPayment: false,
  canCancelPayment: false,
  showMakerColumn: true,
  showEscalatorColumn: true,
  showContactEvidence: true,
  showTicketEvidence: false,
  allowedStatuses: ESCALATOR_STATUSES,
  showTicketColumn: true,
  canFilterByAssignee: true,
  canFilterByEscalator: true,
}

const UNKNOWN_POLICY: PaymentAssignmentRolePolicy = {
  canAssignMaker: false,
  canAssignEscalator: false,
  canAssignPriority: false,
  canBulkAssign: false,
  canUpdateStatus: false,
  canEditPrimaryPaymentMethod: false,
  canSubmitPayment: false,
  canCancelPayment: false,
  showMakerColumn: false,
  showEscalatorColumn: false,
  showContactEvidence: false,
  showTicketEvidence: false,
  allowedStatuses: ALL_STATUSES,
  showTicketColumn: true,
  // Read-only users may filter by any dimension to explore the data.
  canFilterByAssignee: true,
  canFilterByEscalator: true,
}

export function getPaymentAssignmentRolePolicy(
  role: PaymentAssignmentRole
): PaymentAssignmentRolePolicy {
  if (role === PaymentAssignmentRole.CHECKER) return CHECKER_POLICY
  if (role === PaymentAssignmentRole.MAKER) return MAKER_POLICY
  if (role === PaymentAssignmentRole.ESCALATOR) return ESCALATOR_POLICY
  return UNKNOWN_POLICY
}

/**
 * Statuses hidden behind `admin.finance.payment-coordination-enable-clear-by-manual`.
 * When the flag is off, these are removed from every role's `allowedStatuses`.
 */
export const CLEAR_BY_MANUAL_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  PaymentAssignmentStatusId.AP_CONFIRMED,
  PaymentAssignmentStatusId.PAYMENT_CLEARED,
  PaymentAssignmentStatusId.BOT_CLEARED,
])

/**
 * Returns the role policy for the given role, with the three "clear-by-manual"
 * statuses (AP_CONFIRMED, PAYMENT_CLEARED, BOT_CLEARED) filtered out when the
 * `admin.finance.payment-coordination-enable-clear-by-manual` flag is off.
 *
 * Use this hook instead of `getPaymentAssignmentRolePolicy` in React components.
 */
export function usePaymentAssignmentRolePolicy(
  role: PaymentAssignmentRole
): PaymentAssignmentRolePolicy {
  const { flags } = useFeatureFlags()
  const enableClearByManual =
    flags['admin.finance.payment-coordination-enable-clear-by-manual']

  const base = getPaymentAssignmentRolePolicy(role)
  if (enableClearByManual) return base

  return {
    ...base,
    allowedStatuses: base.allowedStatuses.filter(
      (s) => !CLEAR_BY_MANUAL_STATUSES.has(s)
    ),
  }
}
